/* global SENTRY_ENV */
/* global SENTRY_DSN */

/*
 * This file is imported prior to every entry point. It ensures
 * that polyfills are pulled in and that jQuery is properly exposed.
 */
import '@babel/polyfill';

/*
 * Import jQuery and moment so they are picked up by the
 * expose-loader and exposed in the global scope.
 */
import 'jquery';
import moment from 'moment';

/*
 * Set up Sentry for error handling.
 */
import * as Sentry from '@sentry/browser';
import { RewriteFrames } from '@sentry/integrations';
import { htmlTreeAsString } from '@sentry/utils';

import { isOldBrowser } from 'javascripts/browser_detect';

const sentryEnv = SENTRY_ENV;
const sentryDsn = SENTRY_DSN;

// Detect whether the app is running as a locally downloaded file.
// If it is, disable sentry to avoid all the noise a local copy generates.
const isLocalFile = window?.location?.protocol === 'file:';

if (
  ['production', 'staging'].includes(sentryEnv) &&
  sentryDsn &&
  !isLocalFile
) {
  // Ignore errors that include these patterns in the stack trace:
  //   1. Browser extensions
  const STACK_IGNORE_REGEX =
    /((chrome|moz|ms-browser|safari(-web)?))-extension:\/\//;

  // Ignore errors if they include any of the following URLs in the stack trace
  const STACK_IGNORE_STRINGS = [
    // Develop extension contributions
    '/extension_contributions.js',
    // Google translate
    'https://translate.googleapis.com/',
  ];

  Sentry.init({
    dsn: sentryDsn,
    integrations: [new RewriteFrames()],
    autoSessionTracking: false,
    release: window.sentryRelease,
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === 'fetch') {
        const requestId = hint?.response?.headers?.get('x-request-id');
        if (
          !requestId ||
          !window.Configatron?.pod ||
          !window.Configatron?.secureUrlBase
        ) {
          return breadcrumb;
        }

        if (!breadcrumb.data) {
          breadcrumb.data = {};
        }

        const pdt = moment.utc().format('YYYY-MM-DD-H');
        const requestUUID = `web.${pdt.replace(/-/g, '')}.${
          window.Configatron.pod
        }.${requestId}`;
        breadcrumb.data.requestUUID = requestUUID;
      }

      return breadcrumb;
    },
    beforeSend(event, hint) {
      // Ignore errors if the assets failed to load
      if (window.ahaLoaded === false) {
        return null;
      }

      // Ignore errors from browsers with versions older than those specified here
      if (isOldBrowser(window.navigator.userAgent)) {
        return null;
      }

      const error = hint?.originalException;
      const stack = error?.stack || '';

      const message = String(event.message || error?.message);

      if (
        STACK_IGNORE_REGEX.test(stack) ||
        STACK_IGNORE_STRINGS.some(str => stack.includes(str))
      ) {
        return null;
      }

      // In portals, a lot of errors occur if the customer loads their own jQuery, try and ignore those errors
      if (window.portalAssets?.js) {
        // Ignore the error unless the error is coming from our JS code, or from our own origin
        if (
          !stack.includes(location.origin) ||
          !window.portalAssets.js.some(url => stack.includes(url))
        ) {
          console.error(
            'Ignoring JavaScript error since it does not appear to be from Aha! code.',
            error
          );
          return null;
        }
      }

      // Ignore postMessage Google API errors in our admin tool
      if (
        message.includes('postMessage') &&
        String(stack).includes('https://apis.google.com/') &&
        window.location.origin === window.Configatron?.secureUrlBase
      ) {
        return null;
      }

      // Ignore errors that occurred from an idea portal's custom JS
      if (window.ignoreEvalErrors && stack && /\beval\b/.test(stack)) {
        return null;
      }

      // Ignore legacy non chrome edge
      if (window.navigator.userAgent.match(/Edge\//)) {
        return null;
      }

      // Ignore Safari extension errors - see https://github.com/getsentry/sentry-javascript/discussions/5875
      if (stack.includes('@webkit-masked-url') && stack.includes('//hidden/')) {
        return null;
      }

      // Ignore popover-related errors for browsers that don't support it anyway
      if (
        !('showPopover' in HTMLElement.prototype) &&
        ['showPopover', 'hidePopover', ':popover-open'].some(text =>
          message.includes(text)
        )
      ) {
        return null;
      }

      event.logger = 'javascript';
      return event;
    },
    dataCallback: data => {
      const ex =
        data.exception && data.exception.values && data.exception.values[0];

      if (ex) {
        // A very naive fingerprint based solely on the exception type and message.
        // This might prove to be far too simple.
        data.fingerprint = [
          `${ex.type || 'UnknownType'}: ${ex.value || 'no error message'}`,
        ];
      }

      return data;
    },
    ignoreErrors: [
      /^ResizeObserver loop limit exceeded$/,
      /^ResizeObserver loop completed with undelivered notifications/,
      /^resolveToTextNode$/,
      /^Non-Error exception captured/, // caused by rejection raised with a non-error object
      /^Non-Error promise rejection captured/, // caused by rejection raised with a non-error object
      /^<unknown>$/,
      // intermittent network issues, nothing really actionable
      /Loading CSS chunk \d/,
      /ChunkLoadError/,
      /Object Not Found Matching Id/, // meaningless error we receive in the millions. https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/14
      // Harmless error from one browser type that will hopefully go away
      // sometime in the future https://stackoverflow.com/questions/69261499/what-is-instantsearchsdkjsbridgeclearhighlight
      // Adding this to the ignore list because it is coming in with many
      // different signatures.
      /^Can't find variable: instantSearchSDKJSBridgeClearHighlight$/,
      // Every drag on Starter Roadmap causes the below error
      //   We must be using dnd wrong from some first principle.
      //   Many of us have looked at this to figure out where we might fix,
      //   with no luck. So, we're going to ignore it because
      //   that roadmap needs a rewrite anyway.
      /Expected to find a valid target/,
      /Trouble reading csrf token/,
      /status code 40(1|3|4)/,
      /Failed to fetch/,
      /FetchQueue emptied, cancelling all in-progress fetches/,
      /Fetch is aborted/,
      /The operation was aborted/,
      /NAVIGATED_AWAY: Request aborted, user navigated away/,
      /Authorization cancelled./, // User cancels extension OAuth mid-flow - not actionable
      // Error messsage triggerd by browser extensions
      /Could not establish connection\. Receiving end does not exist\./,
    ],
  });

  Sentry.configureScope(scope => {
    scope.setTag('environment', sentryEnv);
  });

  const currentContext = () => {
    return window.location.pathname.match(/^\/(shared)|(published)\//)
      ? 'shared'
      : 'inApp';
  };

  document.addEventListener('DOMContentLoaded', () => {
    const userContext = {};

    if (window.currentUser) {
      const { id, dateFormat } = window.currentUser;
      userContext.id = id;
      userContext.dateFormat = dateFormat;
    }

    Sentry.configureScope(scope => {
      scope.setUser(userContext);

      if (window.currentAccount?.id) {
        scope.setTag('account_id', window.currentAccount.id);
      }

      scope.setTag('context', currentContext());
    });
  });

  document.addEventListener('dragstart', event => {
    const target = htmlTreeAsString(event.target);
    Sentry.addBreadcrumb({
      level: 'info',
      type: 'user',
      category: 'UI drag start',
      message: target,
    });
  });

  document.addEventListener('drop', event => {
    const target = htmlTreeAsString(event.target);
    Sentry.addBreadcrumb({
      level: 'info',
      type: 'user',
      category: 'UI drop',
      message: target,
    });
  });

  window.addEventListener('unhandledrejection', event => {
    Sentry.addBreadcrumb({
      level: 'error',
      type: 'error',
      category: 'unhandled rejection',
      message: event.reason,
    });
  });
}
